import React, { useEffect, useState } from "react";
// ANT DESIGN COMPONENTS
import { Modal, Button, Input, Spin } from "antd";
// I18N TRANSLATION
import { useTranslation } from "react-i18next";
// FONT AWESOME LIBRARY AND ICONS
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTriangleExclamation } from "@fortawesome/free-solid-svg-icons";
// REDUX
import { useDispatch, useSelector } from "react-redux";
import { useDeleteVendorMutation } from "../../../redux/configurationFiles/configurationFilesAPI";
import { updateOpenModalDeleteVendor } from "../../../redux/tools/toolsSlice";
// AUTHORIZATION
import GETJwtToken from "../../../redux/authentication/authentication";
// UTILS
import { styleDeleteButton } from "../../../utils/utils";
// COMPONENTS
import GlobalAlert2 from "../../home/GlobalAlert2";
// INTERFACES
import { RootState } from "../../../app/store";

function VendorsDeleteModal() {
	// ************************************************ */
	// GLOBAL VARIABLES ******************************* */
	const dispatch = useDispatch();
	const [t] = useTranslation("global");

	// ************************************************ */
	// USE STATE VARIABLES **************************** */
	const [disableButtonConfirmDelete, setDisableButtonConfirmDelete] = useState(true);
	const [confirmVendorName, setConfirmVendorName] = useState("");

	// ************************************************ */
	// REDUX SLICE VARIABLES ************************** */
	const { vendor, openModalDeleteVendor } = useSelector((state: RootState) => state.tool);
	const { theme } = useSelector((state: RootState) => state.home);

	// ************************************************ */
	// SERVICES AND API CALLS ************************* */
	const [triggerDeleteVendor, { isLoading, isSuccess, error, isError, reset: resetDeleteVendor }] =
		useDeleteVendorMutation();

	// ************************************************ */
	// FUNCTIONS ************************************** */
	const onClickClose = () => {
		dispatch(updateOpenModalDeleteVendor(false));
	};

	const handleDeleteItem = async () => {
		const token = await GETJwtToken();
		triggerDeleteVendor({ id: vendor.id, token });
	};

	const handleSubmitDeleteItem = async (event: any) => {
		if (event.key === "Enter" && !disableButtonConfirmDelete) {
			const token = await GETJwtToken();
			triggerDeleteVendor({ id: vendor.id, token });
		}
	};

	// ************************************************* */
	// USE EFFECT ************************************** */
	useEffect(() => {
		if (openModalDeleteVendor) {
			setDisableButtonConfirmDelete(true);
			setConfirmVendorName("");
			resetDeleteVendor();
		}
	}, [openModalDeleteVendor]);

	useEffect(() => {
		setDisableButtonConfirmDelete(true);
		if (confirmVendorName && vendor && vendor.name && confirmVendorName.toLowerCase() === vendor.name.toLowerCase()) {
			setDisableButtonConfirmDelete(false);
		} else {
			setDisableButtonConfirmDelete(true);
		}
	}, [confirmVendorName]);

	useEffect(() => {
		onClickClose();
	}, [isSuccess, isError]);

	// ************************************************ */
	// COMPONENT ************************************** */
	return (
		<Modal onOk={onClickClose} closable={false} onCancel={onClickClose} open={openModalDeleteVendor} footer={null}>
			<Spin spinning={isLoading}>
				<div>
					<div className='text-center'>
						<FontAwesomeIcon
							icon={faTriangleExclamation}
							className='generalStyles__warning generalStyles__deleteItemIcon'
						/>
						<p className='mt-3 drawer__text'>
							{t("organizationManagement.deleteDescription.text1")}
							<b className='generalStyles__warning'>{t("organizationManagement.deleteDescription.text2")} </b>
							{t("configuration.vendor.theVendor")}{" "}
							<b className='generalStyles__warning'>{vendor && vendor.name ? vendor.name : ""} </b>
							{t("configuration.vendor.confirmVendorName")}{" "}
						</p>
						<Input
							placeholder={t("general.name")}
							onChange={(e) => {
								setConfirmVendorName(e.target.value);
							}}
							value={confirmVendorName}
							onKeyDown={handleSubmitDeleteItem}
							status='warning'
						/>
					</div>
					<div className='generalStyles__flexEnd mt-4'>
						<div>
							<Button
								htmlType='submit'
								className={styleDeleteButton(disableButtonConfirmDelete, theme)}
								onClick={handleDeleteItem}
								disabled={disableButtonConfirmDelete}
							>
								{t("organizationManagement.delete")}
							</Button>
							<Button onClick={onClickClose} className='buttonStyle__4'>
								{t("organizationManagement.close")}
							</Button>
						</div>
					</div>
				</div>
			</Spin>
			{/** ---------------------------------------------------- */}
			{/** GLOBAL ALERT */}
			<GlobalAlert2 isError={isError} isSuccess={isSuccess} requestType='DELETE' error={error} name='Vendor' />
		</Modal>
	);
}

export default VendorsDeleteModal;
